import React from 'react';
import { motion } from 'framer-motion';
import { ClockIcon, ShieldIcon, HeartPulseIcon, MessageSquareIcon, FileTextIcon, ZapIcon } from 'lucide-react';
export function SolutionSection() {
  return <section className="w-full bg-[#0f1629] py-20 md:py-28" data-id="element-236">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-237">
        {/* The Unique Mechanism */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-238">
          <span className="inline-block text-[#c9a96e] text-sm font-semibold tracking-wider uppercase mb-4" data-id="element-239">
            La Solución
          </span>
          <h2 className="font-['Playfair_Display'] text-3xl sm:text-4xl lg:text-5xl font-bold text-[#f0ebe3] mb-6" data-id="element-240">
            El Protocolo <span className="text-[#c9a96e]" data-id="element-241">Blackout</span>
          </h2>
          <p className="font-['Playfair_Display'] text-xl sm:text-2xl text-[#d4a574] italic mb-6" data-id="element-242">
            (Apagón de 72 Horas)
          </p>
          <div className="max-w-3xl mx-auto" data-id="element-243">
            <p className="text-[#f0ebe3]/85 text-lg leading-relaxed mb-4" data-id="element-244">
              Este no es un libro de consejos motivacionales. Es un{' '}
              <span className="text-[#c9a96e] font-semibold" data-id="element-245">
                cortocircuito deliberado
              </span>{' '}
              para tu sistema nervioso.
            </p>
            <p className="text-[#f0ebe3]/85 text-lg leading-relaxed" data-id="element-246">
              El Blackout es un método diseñado para romper el bucle químico de
              la obsesión en exactamente tres días. No buscamos que lo olvides
              hoy; buscamos{' '}
              <span className="text-[#d4a574] font-semibold" data-id="element-247">
                bajar tus niveles de cortisol
              </span>{' '}
              para que el pánico se detenga y tu dignidad regrese al mando.
            </p>
          </div>
        </motion.div>

        {/* What You Get */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        delay: 0.2
      }} data-id="element-248">
          <h3 className="font-['Playfair_Display'] text-2xl sm:text-3xl font-bold text-[#f0ebe3] text-center mb-12" data-id="element-249">
            Lo Que Recibirás
          </h3>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-250">
            <div className="bg-[#1a2342]/60 p-6 rounded-xl border border-[#c9a96e]/20" data-id="element-251">
              <div className="flex items-start gap-4" data-id="element-252">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#c9a96e]/20 to-[#d4a574]/20 flex items-center justify-center flex-shrink-0" data-id="element-253">
                  <ClockIcon className="w-6 h-6 text-[#c9a96e]" data-id="element-254" />
                </div>
                <div data-id="element-255">
                  <h4 className="font-semibold text-[#f0ebe3] text-lg mb-2" data-id="element-256">
                    El Plan de Acción Horario
                  </h4>
                  <p className="text-[#a0998e] leading-relaxed" data-id="element-257">
                    Qué hacer minuto a minuto durante las primeras 24 horas (las
                    más peligrosas).
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1a2342]/60 p-6 rounded-xl border border-[#c9a96e]/20" data-id="element-258">
              <div className="flex items-start gap-4" data-id="element-259">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#c9a96e]/20 to-[#d4a574]/20 flex items-center justify-center flex-shrink-0" data-id="element-260">
                  <ShieldIcon className="w-6 h-6 text-[#c9a96e]" data-id="element-261" />
                </div>
                <div data-id="element-262">
                  <h4 className="font-semibold text-[#f0ebe3] text-lg mb-2" data-id="element-263">
                    El Protocolo de Blindaje Digital
                  </h4>
                  <p className="text-[#a0998e] leading-relaxed" data-id="element-264">
                    Cómo configurar tu entorno para que sea imposible recaer en
                    la tentación.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1a2342]/60 p-6 rounded-xl border border-[#c9a96e]/20" data-id="element-265">
              <div className="flex items-start gap-4" data-id="element-266">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#c9a96e]/20 to-[#d4a574]/20 flex items-center justify-center flex-shrink-0" data-id="element-267">
                  <HeartPulseIcon className="w-6 h-6 text-[#c9a96e]" data-id="element-268" />
                </div>
                <div data-id="element-269">
                  <h4 className="font-semibold text-[#f0ebe3] text-lg mb-2" data-id="element-270">
                    La Dieta de Dopamina
                  </h4>
                  <p className="text-[#a0998e] leading-relaxed" data-id="element-271">
                    Acciones físicas específicas para calmar los temblores y la
                    ansiedad en el pecho.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1a2342]/60 p-6 rounded-xl border border-[#c9a96e]/20" data-id="element-272">
              <div className="flex items-start gap-4" data-id="element-273">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#c9a96e]/20 to-[#d4a574]/20 flex items-center justify-center flex-shrink-0" data-id="element-274">
                  <MessageSquareIcon className="w-6 h-6 text-[#c9a96e]" data-id="element-275" />
                </div>
                <div data-id="element-276">
                  <h4 className="font-semibold text-[#f0ebe3] text-lg mb-2" data-id="element-277">
                    Guía de Respuestas de Emergencia
                  </h4>
                  <p className="text-[#a0998e] leading-relaxed" data-id="element-278">
                    Qué hacer exactamente si él te escribe mientras estás en el
                    proceso.
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-[#1a2342]/60 p-6 rounded-xl border border-[#c9a96e]/20 md:col-span-2" data-id="element-279">
              <div className="flex items-start gap-4" data-id="element-280">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-[#c9a96e]/20 to-[#d4a574]/20 flex items-center justify-center flex-shrink-0" data-id="element-281">
                  <FileTextIcon className="w-6 h-6 text-[#c9a96e]" data-id="element-282" />
                </div>
                <div data-id="element-283">
                  <h4 className="font-semibold text-[#f0ebe3] text-lg mb-2" data-id="element-284">
                    Worksheets de Realidad
                  </h4>
                  <p className="text-[#a0998e] leading-relaxed" data-id="element-285">
                    Ejercicios para romper la fantasía de "lo que pudo ser" y
                    ver la relación como realmente fue.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}